// src/modules/IMC/HelpAgent/openHelpAgentPopup.ts

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import { Member } from 'modules/common/interfaces/member';
import { getMemberMetadata } from 'modules/IMC/Decagon/utils';

interface GenerateSignatureFunction {
    (data: { id: string }): Promise<{ signature: string; epoch: number; id: string }>;
}

// Module-scoped variable to track the current message listener for cleanup.
let messageListener: ((e: MessageEvent) => void) | null = null;

/**
 * Removes the existing message listener from the window object.
 */
function removeMessageListener() {
    if (messageListener) {
        window.removeEventListener('message', messageListener);
        messageListener = null;
    }
}

export default function openHelpAgentPopup(
    conversationId: string,
    message: string,
    onCloseCallback: () => void, // Callback to notify parent of closure
    memberObj: Member,
    accessToken: string, // Changed from 'token' object to just the string access token
    generateSignature: GenerateSignatureFunction, // Pass the mutateAsync function
    closeWidget?: () => void, // Optional callback to close the Decagon widget
    /**
     * Conversation metadata the AOP routes on (e.g. `page`), merged into the setMetadata call below.
     *
     * Required because `setMetadata` REPLACES the whole metadata object rather than merging: without
     * this, the keys the backend attached at /conversation/new are wiped by the member metadata sent
     * here, the routing key disappears before the AOP evaluates, and the default AOP answers instead.
     */
    conversationMetadata?: Record<string, unknown>,
    isBusinessContextEnabled?: boolean, // `decagon_business_context_metadata` gate, read by the caller
    isPayrollDashboardV2?: boolean, // `payroll_dashboard_v2` gate, read by the caller
    isPayrollDashboardV2ShowLegacy?: boolean // `payroll_dashboard_v2_show_legacy` gate, read by the caller
) {
    // 1. Force cleanup of any existing duet instance and listener at the very beginning
    // This ensures a fresh start for every click
    removeMessageListener();

    // popup is mutual exclusive to help agent widget
    if (window.decagonEmbed) {
        closeWidget?.();
    }
    // close any existing duet instance also to reinitialize conversation
    if (window.duet && typeof window.duet.close === 'function') {
        window.duet.close();
    }

    // The canned message must be sent exactly once per popup invocation. Decagon
    // emits multiple init-style events (DUET_INITIALIZE, CONVERSATION_DESTINATION_CHANGED),
    // and DUET_INITIALIZE itself can fire multiple times — without this gate, the
    // user's auto-message ends up duplicated in the chat (and on Decagon's side).
    let hasSentInitialMessage = false;
    const sendInitialMessage = () => {
        if (hasSentInitialMessage) {
            return;
        }
        hasSentInitialMessage = true;
        window.duet?.sendMessage(message);
    };

    // 2. Define the callback to handle Decagon events
    const callback = (event: { eventName: string; payload?: any; conversationId?: string }) => {
        try {
            switch (event.eventName) {
                case 'DECAGON_LOADED':
                    window.duet?.openConversation(conversationId);
                    break;

                case 'DUET_INITIALIZE':
                    sendInitialMessage();
                    break;

                case 'DUET_CLOSED':
                case 'DUET_DEINITIALIZED':
                    removeMessageListener();
                    onCloseCallback();
                    break;

                case 'DUET_RESPONSE':
                    break;

                case 'CONVERSATION_LOADED':
                    // only send message when conversation is loaded with the exact conversation id
                    if (event.conversationId === conversationId) {
                        window.duet?.open();
                    }
                    break;

                case 'CONVERSATION_DESTINATION_CHANGED':
                    sendInitialMessage();
                    break;

                case 'DECAGON_NEW_CONVERSATION':
                    break;
                default:
                    break;
            }
        } catch (error) {
            sendDatadogRumError('Failed to handle Decagon Popup event', error, {
                memberId: memberObj?.id,
            });
        }
    };

    // 3. Set up the message listener to catch Decagon events from the iframe
    const listener = (e: MessageEvent) => {
        const { data } = e;
        const allowedTypes = ['USER_ACTION', 'DECAGON_EVENT'];

        if (data && typeof data === 'object' && allowedTypes.includes(data.type) && data.payload) {
            callback(data.payload);
        }
    };

    window.addEventListener('message', listener);
    messageListener = listener;

    // 4. Define the Decagon initialization logic
    const initDecagon = async () => {
        try {
            if (!window.duet) {
                throw new Error('Decagon (window.duet) not found after script load');
            }

            // Configure initial behavior if not already ready
            if (!window.duet.isReady()) {
                window.duet.setTriggerMessage('...', false);
                window.duet.setProactiveMessage('...');
                window.duet.hide();
            }

            const { signature, epoch, id: userId } = await generateSignature({ id: memberObj.email });

            window.duet.setUserId(userId);
            window.duet.setUserIdAuth(signature, epoch);

            const memberMetadata = getMemberMetadata(
                memberObj,
                accessToken,
                undefined,
                undefined,
                isBusinessContextEnabled,
                isPayrollDashboardV2,
                isPayrollDashboardV2ShowLegacy
            );
            window.duet.setMetadata({ ...memberMetadata, ...conversationMetadata });

            // If duet is already ready, openConversation manually since DECAGON_LOADED won't fire again
            if (window.duet.isReady()) {
                window.duet.openConversation(conversationId);
            }
        } catch (error) {
            sendDatadogRumError('Failed to initialize Decagon Popup', error, {
                memberId: memberObj?.id,
            });

            removeMessageListener();
            onCloseCallback();
        }
    };

    // 5. Execution
    if (window.duet) {
        initDecagon();
    } else if (document.getElementById('decagon-popup-loader')) {
        // Script is already in the document but window.duet is not populated yet.
        // The listener will catch DECAGON_LOADED and trigger openConversation.
    } else {
        const script = document.createElement('script');
        script.src = 'https://decagon.ai/loaders/collective.js';
        script.id = 'decagon-popup-loader';
        script.async = true;

        script.onload = () => {
            initDecagon();
        };

        script.onerror = () => {
            sendDatadogRumError(
                'Failed to load Decagon Popup script',
                new Error('Failed to load Decagon Popup script')
            );
            removeMessageListener();
            onCloseCallback();
        };

        document.body.appendChild(script);
    }
}
